/**
 * CompanyDeleteModal — force-delete confirmation for a company with data.
 *
 * Opened by CompanyFormModal when `DELETE /v1/companies/:id` answers 409
 * (blocking dependencies). Shows the per-table row counts from the 409
 * payload and offers the platform admin the nuclear option: typing the
 * company's exact name unlocks "Smazat firmu i všechna data", which repeats
 * the DELETE with `{ potvrzeni }` — the API then irreversibly removes the
 * company including every dependent row.
 */

import { useEffect, useState } from 'react';
import { Button } from '@/components/Button';
import { Field, Fields } from '@/components/Field';
import { Modal, DialogCrown, HardBlock } from '@/components/Modal';
import { TextInput } from '@/components/TextInput';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { useDeleteCompanyMutation, type CompanyDeleteBlocked, type MgmtCompany } from './api';

interface CompanyDeleteModalProps {
  open: boolean;
  payload: CompanyDeleteBlocked | null;
  company?: MgmtCompany;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful force-delete (closes the parent form too). */
  onDeleted: () => void;
}

const TABLE_LABELS: Record<string, string> = {
  firmy_uzivatele: 'Členství uživatelů',
  zakaznici: 'Zákazníci',
  kontakt: 'Kontakty',
  vozidlo: 'Nová vozidla',
  ojete_vozidlo: 'Ojetá vozidla',
  vozidla_smlouva: 'Smlouvy — vozidla',
  vozidla_nabidka: 'Nabídky — vozidla',
  ojeta_vozidla_smlouva: 'Smlouvy — ojetá',
  ojeta_vozidla_nabidka: 'Nabídky — ojetá',
  ojeta_vozidla_vykup_smlouva: 'Výkupní smlouvy',
  operak_smlouva: 'Smlouvy — operativní leasing',
  operak_nabidka: 'Nabídky — operativní leasing',
  posta_zasilka: 'Pošta — zásilky',
  servis_udalost: 'Servis — události',
  servis_opravce: 'Servis — opraváři',
};

export function CompanyDeleteModal({
  open,
  payload,
  company,
  onOpenChange,
  onDeleted,
}: CompanyDeleteModalProps) {
  const remove = useDeleteCompanyMutation();
  const [potvrzeni, setPotvrzeni] = useState('');

  useEffect(() => {
    if (open) setPotvrzeni('');
  }, [open, company?.id_firmy]);

  const entries = payload
    ? Object.entries(payload.blocking).filter(([, count]) => count > 0)
    : [];

  const nameMatches = company != null && potvrzeni === company.Nazev;

  const submit = () => {
    if (!company || !nameMatches || remove.isPending) return;
    remove.mutate(
      { id: company.id_firmy, potvrzeni },
      {
        onSuccess: (res) => {
          const total = Object.values(res.smazano ?? {}).reduce((sum, n) => sum + n, 0);
          toast.ok(`Firma smazána včetně dat (${total} záznamů)`);
          onOpenChange(false);
          onDeleted();
        },
        onError: (err) => toast.error(apiMessage(err, 'Smazání se nepodařilo.')),
      },
    );
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange} maxWidth={640}>
      <DialogCrown
        tone="danger"
        eyebrow="Smazání firmy s daty"
        eyebrowMeta={company?.Nazev}
        title={
          <>
            Firma má <em>navázaná data</em>.
          </>
        }
        sub="Na firmě jsou navázány záznamy v dalších tabulkách. Můžete je odstranit ručně, nebo firmu smazat i se všemi daty."
      />
      <HardBlock>
        <b>Nevratně smaže firmu a všechna její data</b> — vozidla, smlouvy, zákazníky, kontakty,
        servis… Tuto akci nelze vzít zpět.
      </HardBlock>
      <Modal.Body className="px-8 py-6">
        {entries.length === 0 ? (
          <p className="font-mono text-[12px] text-ink-3">
            Server nevrátil žádnou blokující závislost.
          </p>
        ) : (
          <ul className="divide-y divide-line border border-line bg-paper">
            {entries.map(([table, count]) => (
              <li
                key={table}
                className="flex items-baseline gap-4 px-4 py-2.5 text-[12.5px]"
              >
                <span className="text-ink">{TABLE_LABELS[table] ?? table}</span>
                <span className="ml-auto font-mono text-[11.5px] tabular-nums text-ink-3">
                  {count}×
                </span>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-6">
          <Fields cols={1}>
            <Field label="Pro potvrzení napište přesný název firmy">
              <TextInput
                value={potvrzeni}
                placeholder={company?.Nazev ?? ''}
                autoComplete="off"
                onChange={(e) => setPotvrzeni(e.target.value)}
              />
            </Field>
          </Fields>
        </div>
      </Modal.Body>
      <Modal.Foot
        meta="Esc · zavřít"
        actions={
          <>
            <Button variant="secondary" size="md" onClick={() => onOpenChange(false)}>
              Zrušit
            </Button>
            <Button
              variant="ink"
              size="md"
              onClick={submit}
              disabled={!nameMatches || remove.isPending}
            >
              {remove.isPending ? 'Mažu…' : 'Smazat firmu i všechna data'}
            </Button>
          </>
        }
      />
    </Modal>
  );
}

function apiMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    const e = (err.body as { error?: string }).error;
    if (typeof e === 'string') return e;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}
